import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import moment from 'moment'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { matchPath } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { UpdateVirtualCardScreen } from 'src/app/UpdateVirtualCardForm/UpdateVirtualCardScreen'
import ActionRightFlyoutContent from 'src/components/ActionRightFlyoutContent/ActionRightFlyoutContent'
import {
  CreditCardDropdownInfiniteScrollProps,
  CreditCardOption,
} from 'src/components/CreditCardDropdown/CreditCardDropdown'
import { RecurrenceFormValues } from 'src/components/RecurrenceForm/RecurrenceForm'
import TestId from 'src/components/TestId/TestId'
import { UserOption } from 'src/components/UserDropdown/UserDropdown'
import VirtualCardForm, {
  VirtualCardFlatFormValues,
} from 'src/components/VirtualCardForm/VirtualCardForm'
import { ImageNames, VirtualCardTypes } from 'src/enums'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import { useTranslate } from 'src/localization/Trns'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import {
  RecurrencePeriod,
  RecurrenceTerminator,
} from 'src/models/recurrence.models'
import { UserListModel } from 'src/models/user.models'
import {
  ApprovalStatus,
  VirtualCardSecureModel,
} from 'src/models/virtualCard.models'
import { VirtualCardPermissionsModel } from 'src/models/virtualCardPermissions.models'
import { virtualCardsAllCardsUrl } from 'src/navigation/urls'
import {
  selectNullSafeCurrencyFromState,
  selectUserIdFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectCreditCardListModelsFromState } from 'src/redux/selectors/data/creditCards.selectors'
import { selectSelectedVirtualCardIdsFromState } from 'src/redux/selectors/data/virtualCards.selectors'
import {
  selectCalculatedBackgroundLocation,
  selectQueryParams,
} from 'src/redux/selectors/router.selectors'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { PutVirtualCardRequest } from 'src/requests/putVirtualCard.request'
import { definedUserEmails } from 'src/utils/cardUserEmails.utils'
import { toCents, toDollars } from 'src/utils/currency.utils'
import { extendWeekdays } from 'src/utils/date.utils'
import { ErrorDetailMap } from 'src/utils/error.utils'
import { buildLowLimitAlert } from 'src/utils/lowLimitAlert.utils'
import { css } from 'styled-components'

interface UpdateVirtualCardRecurringContentProps {
  cardApproverId: string | undefined
  virtualCard: VirtualCardSecureModel
  virtualCardPermissions: VirtualCardPermissionsModel
  recipient: UserListModel | undefined
  cardholder: UserListModel | undefined
  currencyError?: boolean
  userOptions: UserOption[]
  isFormDisabled: boolean
  labelCreditCardsAsAccounts: boolean
  creditCardOptions: CreditCardOption[]
  receiptAttachments: ReceiptAttachmentModel[]
  uploadingAttachmentThumbnails: Record<string, string>
  puttingVirtualCard: boolean
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
  putVirtualCardAndOpenToastAndCloseFlyout: (
    id: string,
    body: Partial<PutVirtualCardRequest>,
    openToast?: boolean,
  ) => void
  uploadAttachments: (attachments: PostReceiptAttachmentRequest[]) => void
  deleteAttachment: (attachmentId: string) => void
  setScreen: (val: UpdateVirtualCardScreen) => void
  negativeButton?: {
    loading: boolean
    text: React.ReactNode | string
    onClick: () => void
  }
  creditCardInfiniteScrollProps?: CreditCardDropdownInfiniteScrollProps
  isGuestCard?: boolean
  showSoftAlert?: boolean
  typeofCard?: VirtualCardTypes
}

const UpdateVirtualCardRecurringContent: React.FC<
  UpdateVirtualCardRecurringContentProps
> = (props) => {
  const translate = useTranslate()

  const queryParams = useSelector(selectQueryParams)
  const creditCards = useSelector(selectCreditCardListModelsFromState)
  const defaultCurrency = useSelector(selectNullSafeCurrencyFromState)
  const selectedVirtualCardIdsFromRedux = useSelector(
    selectSelectedVirtualCardIdsFromState,
  )
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const userId = useSelector(selectUserIdFromState)
  const cardTypeRecurringImage = useColorOrGrayscaleImage(
    ImageNames.CardTypeRecurring,
  )
  const isCurrentUserFundingSourceOwner =
    userId === props.virtualCard.cardholderId
  const isCurrentUserCardApprover = userId === props.cardApproverId
  const isCurrentUserCardRequester = userId === props.virtualCard.recipientId
  const isFinalCardApprovalOrRejection =
    isCurrentUserCardApprover &&
    props.virtualCard.awaitingApprovalStatus ===
      ApprovalStatus.AwaitingApproverRuleApproval

  const { analytics } = useAnalyticsContext()

  const handleResetTime = (recurrenceFields: RecurrenceFormValues): string => {
    switch (recurrenceFields.period) {
      case RecurrencePeriod.Weekly:
        return recurrenceFields.interval === 1
          ? 'Week on ' + extendWeekdays(recurrenceFields.byWeekDay)
          : recurrenceFields.interval +
              ' weeks on ' +
              extendWeekdays(recurrenceFields.byWeekDay)
      case RecurrencePeriod.Monthly:
        return recurrenceFields.interval === 1
          ? 'Month on the ' +
              moment.tz(`${recurrenceFields.byMonthDay}-01-3000`).format('Do')
          : recurrenceFields.interval +
              ' months on the ' +
              moment.tz(`${recurrenceFields.byMonthDay}-01-3000`).format('Do')
      case RecurrencePeriod.Daily:
        return recurrenceFields.interval === 1
          ? 'Day'
          : recurrenceFields.interval + ' days'
      default:
        return ' '
    }
  }

  const baseFlatFields = useMemo(
    (): VirtualCardFlatFormValues => ({
      displayName: props.virtualCard.displayName,
      userEmails: props.virtualCardPermissions.canUpdateVirtualCard
        ? definedUserEmails(props.recipient)
        : props.virtualCard.awaitingApprovalStatus ===
            ApprovalStatus.AwaitingApproverRuleApproval
          ? definedUserEmails(props.cardholder, props.recipient)
          : definedUserEmails(props.cardholder),
      creditCardId: props.virtualCard.creditCardId,
      balanceDollars: props.virtualCard.balance,
      notes: props.virtualCard.notes,
      validToMoment: props.virtualCard.validToTimezonedMoment,
      currency: props.virtualCard.currency,
      lowBalanceAlertEnabled:
        props.virtualCard.lowLimitAlert?.alertEnabled ?? false,
      lowBalanceThresholdDollars:
        props.virtualCard.lowLimitAlert?.amountThresholdCents != null
          ? toDollars(props.virtualCard.lowLimitAlert.amountThresholdCents)
          : undefined,
    }),
    [props.virtualCard, props.recipient, props.virtualCardPermissions],
  )

  // const newRequestInitialCreditCardId = props.creditCardOptions.length === 1 && props.virtualCard.canUpdateVirtualCard
  //   ? props.creditCardOptions[0].value
  //   : queryParams['creditCardId']
  const newRequestInitialCreditCardId =
    typeof queryParams.creditCardId === 'string'
      ? queryParams.creditCardId
      : undefined // TODO
  const selectedCreditCard =
    newRequestInitialCreditCardId !== undefined
      ? creditCards[newRequestInitialCreditCardId]
      : undefined
  const initialFlatFields = useMemo(
    (): VirtualCardFlatFormValues => ({
      displayName: props.virtualCard.displayName,
      userEmails: props.virtualCardPermissions.canUpdateVirtualCard
        ? definedUserEmails(props.recipient)
        : props.virtualCard.awaitingApprovalStatus ===
            ApprovalStatus.AwaitingApproverRuleApproval
          ? definedUserEmails(props.cardholder, props.recipient)
          : definedUserEmails(props.cardholder),
      creditCardId:
        props.virtualCard.creditCardId || newRequestInitialCreditCardId,
      balanceDollars: props.currencyError
        ? 0
        : props.virtualCard.pending?.balance || props.virtualCard.balance,
      notes: props.virtualCard.notes,
      validToMoment:
        props.virtualCard.pending?.validToTimezonedMoment(
          props.virtualCard.timezone,
        ) || props.virtualCard.validToTimezonedMoment,
      currency: props.currencyError
        ? defaultCurrency
        : selectedCreditCard?.currency || props.virtualCard.currency,
      receiptRulesExempt: props.virtualCard.receiptRulesExempt,
      lowBalanceAlertEnabled:
        props.virtualCard.lowLimitAlert?.alertEnabled ?? false,
      lowBalanceThresholdDollars:
        props.virtualCard.lowLimitAlert?.amountThresholdCents != null
          ? toDollars(props.virtualCard.lowLimitAlert.amountThresholdCents)
          : undefined,
    }),
    [
      props.virtualCard,
      props.recipient,
      newRequestInitialCreditCardId,
      props.virtualCardPermissions,
    ],
  )

  const baseRecurrenceFields = useMemo(
    (): Partial<RecurrenceFormValues> => ({
      amountDollars:
        props.virtualCard.pending?.recurrence?.balance ||
        props.virtualCard.recurrence?.balance ||
        props.virtualCard.balance,
      recurrenceDollars:
        props.virtualCard.pending?.recurrence?.balance ||
        props.virtualCard.recurrence?.balance ||
        props.virtualCard.balance,
      period: props.virtualCard.recurrence?.period,
      interval: props.virtualCard.recurrence?.interval,
      terminator: props.virtualCard.recurrence?.terminator,
      count:
        props.virtualCard.recurrence?.remainingCount !== undefined
          ? props.virtualCard.recurrence.remainingCount
          : props.virtualCard.recurrence?.count,
      untilMoment: props.virtualCard.recurrence?.untilTimezonedMoment(
        props.virtualCard.timezone,
      ),
      byWeekDay: props.virtualCard.recurrence?.byWeekDay,
      byMonthDay: props.virtualCard.recurrence?.byMonthDay,
      byYearDay: props.virtualCard.recurrence?.byYearDay,
    }),
    [props.virtualCard],
  )

  const initialRecurrenceFields = useMemo(
    (): Partial<RecurrenceFormValues> => ({
      amountDollars:
        props.virtualCard.pending?.recurrence?.balance ||
        props.virtualCard.recurrence?.balance ||
        props.virtualCard.balance,
      recurrenceDollars:
        props.virtualCard.pending?.recurrence?.balance ||
        props.virtualCard.recurrence?.balance ||
        props.virtualCard.balance,
      period:
        props.virtualCard.pending?.recurrence?.period ||
        props.virtualCard.recurrence?.period,
      interval:
        props.virtualCard.pending?.recurrence?.interval ||
        props.virtualCard.recurrence?.interval,
      terminator:
        props.virtualCard.pending?.recurrence?.terminator ||
        props.virtualCard.recurrence?.terminator,
      count:
        props.virtualCard.pending?.recurrence?.remainingCount !== undefined
          ? props.virtualCard.pending.recurrence.remainingCount
          : props.virtualCard.pending?.recurrence?.count !== undefined
            ? props.virtualCard.pending.recurrence.count
            : props.virtualCard.recurrence?.remainingCount !== undefined
              ? props.virtualCard.recurrence.remainingCount
              : props.virtualCard.recurrence?.count,
      untilMoment:
        props.virtualCard.pending?.recurrence?.untilTimezonedMoment(
          props.virtualCard.timezone,
        ) ||
        props.virtualCard.recurrence?.untilTimezonedMoment(
          props.virtualCard.timezone,
        ),
      byWeekDay:
        props.virtualCard.pending?.recurrence?.byWeekDay ||
        props.virtualCard.recurrence?.byWeekDay,
      byMonthDay:
        props.virtualCard.pending?.recurrence?.byMonthDay ||
        props.virtualCard.recurrence?.byMonthDay,
      byYearDay:
        props.virtualCard.pending?.recurrence?.byYearDay ||
        props.virtualCard.recurrence?.byYearDay,
    }),
    [props.virtualCard],
  )

  const onSuccessfulSubmit = (
    flatFields: VirtualCardFlatFormValues,
    expenseDetails: Record<string, string>,
    recurrenceFields: RecurrenceFormValues,
  ): void => {
    const shouldResetCount =
      recurrenceFields.period !== props.virtualCard.recurrence?.period ||
      recurrenceFields.interval !== props.virtualCard.recurrence?.interval ||
      (recurrenceFields.period === RecurrencePeriod.Weekly &&
        recurrenceFields.byWeekDay !==
          props.virtualCard.recurrence?.byWeekDay) ||
      (recurrenceFields.period === RecurrencePeriod.Monthly &&
        recurrenceFields.byMonthDay !==
          props.virtualCard.recurrence?.byMonthDay) ||
      (recurrenceFields.period === RecurrencePeriod.Yearly &&
        recurrenceFields.byMonthDay !==
          props.virtualCard.recurrence?.byYearDay) ||
      recurrenceFields.count !== props.virtualCard.recurrence?.remainingCount ||
      recurrenceFields.untilMoment?.format('YYYY-MM-DD') !==
        props.virtualCard.recurrence
          ?.untilTimezonedMoment(props.virtualCard.timezone)
          ?.format('YYYY-MM-DD') ||
      recurrenceFields.terminator !== props.virtualCard.recurrence?.terminator
    isFinalCardApprovalOrRejection && !isCurrentUserCardRequester
      ? // TODO: add approve action and redux
        props.putVirtualCardAndOpenToastAndCloseFlyout(props.virtualCard.id, {
          pendingRevisionId:
            props.virtualCard?.pending?.id !== undefined
              ? props.virtualCard.pending.id
              : undefined,
        })
      : props.putVirtualCardAndOpenToastAndCloseFlyout(props.virtualCard.id, {
          creditCardId: flatFields.creditCardId,
          currency: flatFields.currency,
          displayName: flatFields.displayName,
          expenseDetails: Object.entries(expenseDetails)
            .filter((keyVal) => !!keyVal[1])
            .map(([key, val]) => ({ categoryId: key, labelId: val })),
          notes: flatFields.notes,
          balanceCents:
            flatFields.balanceDollars !== undefined
              ? toCents(flatFields.balanceDollars)
              : 0,
          recurs: true,
          recurrence: {
            // TODO: maybe don't submit 0 here?
            balanceCents:
              recurrenceFields.recurrenceDollars !== undefined
                ? toCents(recurrenceFields.recurrenceDollars)
                : 0,
            period: recurrenceFields.period,
            interval: recurrenceFields.interval,
            terminator: recurrenceFields.terminator,
            count:
              recurrenceFields.terminator === RecurrenceTerminator.Count
                ? shouldResetCount
                  ? recurrenceFields.count
                  : props.virtualCard.recurrence?.count
                : undefined,
            until:
              recurrenceFields.terminator === RecurrenceTerminator.Date
                ? recurrenceFields.untilMoment?.format('YYYY-MM-DD')
                : undefined,
            byWeekDay:
              recurrenceFields.period === RecurrencePeriod.Weekly
                ? recurrenceFields.byWeekDay
                : undefined,
            byMonthDay:
              recurrenceFields.period === RecurrencePeriod.Monthly
                ? recurrenceFields.byMonthDay
                : undefined,
            byYearDay:
              recurrenceFields.period === RecurrencePeriod.Yearly
                ? recurrenceFields.byYearDay
                : undefined,
            resetCount:
              recurrenceFields.terminator === RecurrenceTerminator.Count
                ? shouldResetCount
                : false,
          },
          receiptAttachmentIds: props.receiptAttachments.map((att) => att.id),
          pendingRevisionId: props.virtualCard.pending?.id,
          receiptRulesExempt: flatFields.receiptRulesExempt,
          lowLimitAlert: buildLowLimitAlert(flatFields),
        })

    const eventName =
      props.virtualCardPermissions.canUpdateVirtualCard ||
      isCurrentUserCardApprover
        ? props.virtualCard.isPending
          ? 'manage vcn request'
          : !props.virtualCard.pending && !props.virtualCard.isPending
            ? 'update vcn'
            : undefined
        : props.virtualCard.isPending
          ? 'update vcn request'
          : props.virtualCard.isActive
            ? 'request vcn update'
            : undefined
    const properties = {
      type_of_card: props.virtualCard.cardTypeLabel.props.source,
      card_name: flatFields.displayName,
      credit_limit: flatFields.balanceDollars,
      active_until: flatFields.validToMoment?.format('YYYY-MM-DD'),
      add_attachments:
        props.receiptAttachments !== undefined &&
        props.receiptAttachments.length > 0,
      add_notes: flatFields.notes !== undefined,
      ...(eventName === 'manage vcn request'
        ? {
            sourceAccount:
              flatFields.creditCardId !== undefined
                ? creditCards[flatFields.creditCardId].displayName
                : undefined,
            resetEnds:
              recurrenceFields.terminator === RecurrenceTerminator.Count
                ? 'Ends after ' + recurrenceFields.count + ' resets'
                : recurrenceFields.terminator === RecurrenceTerminator.Date
                  ? recurrenceFields.untilMoment?.format('MM-DD-YYYY')
                  : 'Never ends',
            resetEvery: recurrenceFields.period
              ? handleResetTime(recurrenceFields)
              : undefined,
            recipientCount: props.recipient?.email !== undefined ? 1 : 0,
            recipients: props.recipient?.email,
            card_Created: true,
            request_status: 'Approve',
          }
        : {}),
      ...(eventName === 'update vcn'
        ? {
            receipt_rules_exempt: flatFields.receiptRulesExempt,
            is_card_creator: props.virtualCard.cardholderId === userId,
            bulk: props.typeofCard === VirtualCardTypes.bulk,
          }
        : {}),
    }
    if (eventName !== undefined) {
      analytics?.track(eventName, properties)
    }
  }

  return (
    <ActionRightFlyoutContent
      title={props.virtualCard.updateFlyoutTitle(props.virtualCardPermissions)}
      specificElements={{
        subtitle: translate(
          props.isGuestCard ? 'Recurring Guest' : 'Recurring',
        ),
        imageSrc: cardTypeRecurringImage,
        changeLink: {
          ariaLabelSource: 'Change Card Type',
          changeMessage: translate('Change Card Type'),
          onChangeClick: (): void => {
            props.setScreen(UpdateVirtualCardScreen.Pick)
          },
        },
      }}
      cardChangeNotAllowed={
        !props.virtualCard.isPending &&
        (props.virtualCard.features.singleExactPay ||
          props.virtualCard.awaitingApprovalStatus ===
            ApprovalStatus.AwaitingApproverRuleApproval)
      }
      permissions={{
        canUpdateVirtualCard:
          props.virtualCardPermissions.canUpdateVirtualCard &&
          !props.virtualCard.isConsumed,
        isCardPending: props.virtualCard.isPending,
      }}
      screen={UpdateVirtualCardScreen.Pick}
    >
      <TestId testId={'updateVirtualCardForm'}>
        <VirtualCardForm
          isRecurringPayment
          topText={
            <>
              {matchPath(
                calculatedBackgroundLocation.pathname,
                virtualCardsAllCardsUrl(),
              ) &&
                selectedVirtualCardIdsFromRedux !== undefined &&
                selectedVirtualCardIdsFromRedux?.length > 1 && (
                  <AlertPanel
                    variant="warning"
                    css={css`
                      margin-bottom: 20px;
                    `}
                  >
                    {translate(
                      'You can only edit virtual cards you have created or have access to as a budget user',
                    )}
                  </AlertPanel>
                )}
              {!isCurrentUserCardApprover
                ? props.virtualCard.updateFormTopText(
                    props.virtualCardPermissions,
                  )
                : undefined}
            </>
          }
          initialFlatFields={initialFlatFields}
          initialRecurrenceFields={initialRecurrenceFields}
          updateProps={{
            baseFlatFields: baseFlatFields,
            baseRecurrenceFields: baseRecurrenceFields,
            timezone: props.virtualCard.timezone,
          }}
          buttonText={props.virtualCard.updateFormButtonText(
            props.virtualCardPermissions,
            isCurrentUserCardApprover,
          )}
          id={props.virtualCard
            .updateFormButtonText(
              props.virtualCardPermissions,
              isCurrentUserCardApprover,
            )
            .props.source.split(' ')
            .join('')}
          onSuccessfulSubmit={onSuccessfulSubmit}
          userOptions={props.userOptions}
          userFieldLabel={
            props.virtualCardPermissions.canUpdateVirtualCard
              ? translate('Recipient')
              : translate('Request From')
          }
          customAddressEditable
          physicalCardEditable
          loading={props.puttingVirtualCard}
          recipientNamesEditable={false}
          recurrenceEditable={true}
          notesEditable={true}
          amountProps={{
            amountEditable: true,
          }}
          creditCardEditable={
            props.virtualCardPermissions.canUpdateVirtualCard &&
            props.virtualCard.isPending &&
            props.virtualCard.awaitingApprovalStatus !==
              ApprovalStatus.AwaitingApproverRuleApproval
          }
          creditCardProps={
            !props.virtualCardPermissions.canUpdateVirtualCard ||
            (isCurrentUserCardApprover && !isCurrentUserFundingSourceOwner)
              ? undefined
              : {
                  isFormDisabled: props.isFormDisabled,
                  creditCardOptions: props.creditCardOptions,
                  labelCreditCardsAsAccounts: props.labelCreditCardsAsAccounts,
                }
          }
          attachmentsProps={{
            receiptAttachments: props.receiptAttachments,
            uploadingAttachmentThumbnails: props.uploadingAttachmentThumbnails,
            uploadAttachments: props.uploadAttachments,
            deleteAttachment: props.deleteAttachment,
          }}
          negativeButton={props.negativeButton}
          topLevelError={props.topLevelError}
          errorDetailMap={props.errorDetailMap}
          currencyError={props.currencyError}
          creditCardInfiniteScrollProps={props.creditCardInfiniteScrollProps}
          canUpdateVirtualCard={
            props.virtualCardPermissions.canUpdateVirtualCard
          }
          typeofCard={
            props.isGuestCard
              ? VirtualCardTypes.recurringGuest
              : VirtualCardTypes.recurring
          }
          showCardApprovalSoftAlert={props.showSoftAlert}
          awaitingFinalApproval={
            props.virtualCard.awaitingApprovalStatus ===
            ApprovalStatus.AwaitingApproverRuleApproval
          }
          awaitingFundingSourceOwnerApproval={
            props.virtualCard.awaitingApprovalStatus ===
            ApprovalStatus.AwaitingCreditCardUserApproval
          }
          cardApproverId={props.cardApproverId}
          virtualCard={props.virtualCard}
          isRequest={props.virtualCard.isPending}
        />
      </TestId>
    </ActionRightFlyoutContent>
  )
}

export default UpdateVirtualCardRecurringContent
